import React, {useEffect, useState} from "react";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import {NotificationManager} from "react-notifications";
import Title from "../dashboard/Title";
import TextInput from "../form/input/TextInput";
import AutocompleteInput from "../form/input/AutocompleteInput";
import Popup from "./Popup";
import ActionButton from "../form/input/ActionButton";
import ConfirmDialog from "./ConfirmDialog";
import UserService from "../../services/user.service";
import {makeStyles} from "@material-ui/core/styles";
import {useDashboardStyles} from "../settings/dashboardStyles";

const emptyValues = {
    _id: null,
    name: "",
    contacts: []
};

const useLocalStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    tableWrapper: {
        width: "100%",
        overflowX: "auto",
    },
    formStack: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    contactsCell: {
        color: "#5a6675",
        lineHeight: 1.5,
    },
    actionsCell: {
        whiteSpace: "nowrap",
        width: 1,
    },
}));

export default function ContactGroupList() {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();
    const [contacts, setContacts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [values, setValues] = useState(emptyValues);
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState(false);
    const [page, setPage] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState({isOpen: false, title: '', subTitle: ''});

    const loadData = () => {
        UserService.getContacts(0).then((response) => setContacts(response.data), () => setContacts([]));
        UserService.getContactGroups().then((response) => setGroups(response.data), () => setGroups([]));
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (page > 0 && page * 5 >= groups.length) {
            setPage(page - 1);
        }
    }, [groups.length, page]);

    const contactOptions = contacts.map((contact) => ({
        ...contact,
        title: `${contact.name || 'Kontakt'}${contact.email ? ` — ${contact.email}` : ''}${contact.phone ? ` — ${contact.phone}` : ''}`
    }));
    const rowsPerPage = 5;
    const visibleGroups = groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const resetToDefault = () => {
        setValues(emptyValues);
        setErrors({});
        setShow(false);
    };

    const validate = () => {
        const temp = {};
        temp.name = values.name ? "" : "Vyplňte názov skupiny.";
        setErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    const handleSave = (event) => {
        event.preventDefault();
        if (!validate()) return;

        const data = {
            _id: values._id,
            name: values.name,
            contacts: values.contacts.map((contact) => contact._id)
        };
        const request = values._id ? UserService.updateContactGroup(data) : UserService.addContactGroup(data);

        request.then(() => {
            NotificationManager.success(values._id ? 'Skupina upravená!' : 'Skupina vytvorená!', values._id ? 'Upravené' : 'Vytvorené', 5000);
            resetToDefault();
            loadData();
        }, () => {
            NotificationManager.error('Skupinu sa nepodarilo uložiť.', 'Chyba', 5000);
        });
    };

    const handleEdit = (group) => () => {
        setValues({
            _id: group._id,
            name: group.name || '',
            contacts: (group.contacts || []).filter(Boolean).map((contact) => ({
                ...contact,
                title: `${contact.name || 'Kontakt'}${contact.email ? ` — ${contact.email}` : ''}${contact.phone ? ` — ${contact.phone}` : ''}`
            }))
        });
        setShow(true);
    };

    const handleDelete = (id) => {
        setConfirmDialog({...confirmDialog, isOpen: false});
        UserService.deleteContactGroup(id).then(() => {
            NotificationManager.success('Skupina vymazaná!', 'Vymazané', 5000);
            loadData();
        }, () => {
            NotificationManager.error('Skupinu sa nepodarilo odstrániť.', 'Chyba', 5000);
        });
    };

    return (
        <Paper className={`${classes.paper} ${localClasses.paper}`} elevation={0}>
            <div className={classes.sectionHeader}>
                <Title>Skupiny kontaktov</Title>
                <div className={classes.sectionToolbar}>
                    <Button
                        startIcon={<AddIcon/>}
                        className={classes.primaryAction}
                        onClick={() => setShow(true)}
                    >
                        Pridať skupinu
                    </Button>
                </div>
            </div>

            {groups.length === 0 ? (
                <div className={classes.emptyState}>Zatiaľ nemáte pridané žiadne skupiny kontaktov.</div>
            ) : (
                <div className={localClasses.tableWrapper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Názov</TableCell>
                                <TableCell>Kontakty</TableCell>
                                <TableCell align="right">Akcie</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleGroups.map((group) => (
                                <TableRow key={group._id}>
                                    <TableCell><strong>{group.name}</strong></TableCell>
                                    <TableCell className={localClasses.contactsCell}>{(group.contacts || []).map((contact) => contact.name).filter(Boolean).join(', ')}</TableCell>
                                    <TableCell align="right" className={localClasses.actionsCell}>
                                        <ActionButton color="primary">
                                            <Tooltip title="Upraviť skupinu">
                                                <EditIcon onClick={handleEdit(group)} fontSize="small"/>
                                            </Tooltip>
                                        </ActionButton>
                                        <ActionButton color="secondary">
                                            <Tooltip title="Vymazať skupinu">
                                                <DeleteIcon
                                                    fontSize="small"
                                                    onClick={() => setConfirmDialog({
                                                        isOpen: true,
                                                        title: 'Naozaj chcete vymazať túto skupinu?',
                                                        subTitle: 'Túto operáciu nevieš vrátiť.',
                                                        onConfirm: () => handleDelete(group._id)
                                                    })}
                                                />
                                            </Tooltip>
                                        </ActionButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={groups.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[]}
                        onChangePage={(event, newPage) => setPage(newPage)}
                    />
                </div>
            )}

            <Popup
                title={values._id ? 'Úprava skupiny' : 'Pridanie skupiny'}
                openPopup={show}
                setOpenPopup={setShow}
                additionalWork={resetToDefault}
                maxW="sm"
                actions={
                    <Button
                        variant="contained"
                        type="submit"
                        form="contact-group-form"
                        className={classes.primaryAction}
                    >
                        {values._id ? 'Upraviť skupinu' : 'Pridať skupinu'}
                    </Button>
                }
            >
                <form id="contact-group-form" className={localClasses.formStack} autoComplete="off" onSubmit={handleSave}>
                    <TextInput
                        label="Názov skupiny"
                        error={errors.name}
                        name="name"
                        value={values.name}
                        onChange={(event) => setValues({...values, name: event.target.value})}
                        fullWidth
                    />
                    <AutocompleteInput
                        label="Kontakty"
                        name="contacts"
                        value={values.contacts}
                        options={contactOptions}
                        onChange={(event, selectedContacts) => setValues({...values, contacts: selectedContacts})}
                        multiple
                        fullWidth
                        filterSelectedOptions
                    />
                </form>
            </Popup>

            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
                icon={<DeleteIcon/>}
            />
        </Paper>
    );
}
